'use client';

import { memo, useCallback } from 'react';
import { useProjectSortBy } from '@frameio/shared/hooks';
import type { ProjectSortByField } from '@frameio/shared/resources/projects/types';
import {
  Box,
  IconButton2,
  Menu,
  MenuButton,
  MenuGroup,
  MenuRadio,
  MenuSelect,
  Stack,
  Text,
  theme,
} from '@frameio/vapor';
import { IconArrowUpDown, IconSort } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/ProjectSwitcherSheetSort';

const MENU_WIDTH = 256;

TEXT.DEFINE({
  /** Menu label for sorting by Date Created */
  dateCreated: 'Date Created',
  /** Menu label for sorting by name */
  name: 'Name',
  /** Menu group label for sorting options in the project switcher sheet */
  sortBy: 'Sort by',
  /** Accessibility label for the sort button, including the currently selected sort field */
  sortByLabel: (label?: string) => `Sort by ${label}`,
  sortByOrderLabel: {
    default: {
      /** Sort order label for sorting project names A to Z */
      asc: 'from A → Z',
      /** Sort order label for sorting project names Z to A */
      desc: 'from Z → A',
    },
    created_at: {
      /** Sort order label for sorting by creation date from oldest to newest */
      asc: 'from Oldest → Newest',
      /** Sort order label for sorting by creation date from newest to oldest */
      desc: 'from Newest → Oldest',
    },
  },
});

export const ProjectSwitcherSheetSort = memo(() => {
  const [sortBy, setSortBy] = useProjectSortBy();

  const handleSortByFieldSelect = useCallback(
    (field: ProjectSortByField) => {
      setSortBy({ field, order: sortBy.order });
    },
    [setSortBy, sortBy.order]
  );

  const handleToggleSortByOrder = useCallback(() => {
    setSortBy({
      field: sortBy.field,
      order: sortBy.order === 'asc' ? 'desc' : 'asc',
    });
  }, [setSortBy, sortBy.order, sortBy.field]);

  const sortByLabel =
    sortBy.field === 'created_at' ? TEXT.dateCreated : TEXT.name;

  const sortOrderLabel =
    sortBy.field === 'created_at'
      ? TEXT.sortByOrderLabel.created_at[sortBy.order]
      : TEXT.sortByOrderLabel.default[sortBy.order];

  return (
    <Menu
      autoHide={false}
      content={
        <MenuGroup label={TEXT.sortBy}>
          <Stack gap={8}>
            <MenuSelect
              content={
                <>
                  <MenuRadio
                    key={TEXT.name}
                    checked={sortBy.field === 'default'}
                    name="default"
                    onPress={() => handleSortByFieldSelect('default')}
                  >
                    {TEXT.name}
                  </MenuRadio>

                  <MenuRadio
                    key={TEXT.dateCreated}
                    checked={sortBy.field === 'created_at'}
                    name="created_at"
                    onPress={() => handleSortByFieldSelect('created_at')}
                  >
                    {TEXT.dateCreated}
                  </MenuRadio>
                </>
              }
              data-testid="sort-by-field-select"
              hideLabel={true}
              solidBackground={true}
              value={
                sortBy.field === 'created_at' ? TEXT.dateCreated : TEXT.name
              }
              width={`${MENU_WIDTH - 16}px`}
            >
              <Box flexGrow="1">
                <Text variant="body">{sortByLabel}</Text>
              </Box>
            </MenuSelect>
            <MenuButton
              data-testid="sort-by-order-button"
              iconAfter={
                <IconArrowUpDown color={theme.colors.icon.primary} size={16} />
              }
              onClick={handleToggleSortByOrder}
              shouldCloseMenuOnSelect={false}
              solidBackground={true}
              style={{ textAlign: 'left' }}
            >
              <Box alignItems="center" display="inline-flex" flexGrow="1">
                <Text variant="body">{sortOrderLabel}</Text>
              </Box>
            </MenuButton>
          </Stack>
        </MenuGroup>
      }
      minWidth={`${MENU_WIDTH}px`}
      placement="bottom-start"
    >
      <IconButton2 icon={<IconSort />} label={sortByLabel} variant="tertiary" />
    </Menu>
  );
});
